import { Relation } from '@/app/api/internalAPIs'; 
import translate from '@/app/utils/translate';
import {
  getEmployeeInfoById,
  isHR,
} from '@/old/utils/helper';
import { getEnumName } from '@/app/redux/enums';
import {
  EvaluationStatus,
  EvaluationStatusDistribution,
  GenericEmployeeEvaluation,
  GenericEvaluationItem,
  ITemplate,
  ITemplateFull,
  ITemplateReport,
  ITemplateReportData,
  IUnitGridData,
  ReportEvaluationType,
  SummaryEmpData,
  TemplateFieldSelection,
  TemplateStatus,
} from './types';
import { OrgUnit } from '@/app/hooks/useUnits';

const emptyDistribution: EvaluationStatusDistribution = Object.freeze({
  inProgress: [],
  completed: [],
  notStarted: [],
});

export const filterEvaluations = (
  rawEvaluations: ReportEvaluationType[],
  myUnits: number[],
  selectedTemplateIds: string[],
): ReportEvaluationType[] => {
  if (!selectedTemplateIds.length || myUnits.length === 0) {
    return [];
  }

  return rawEvaluations.filter((appraisal: ReportEvaluationType) => {
    if (!myUnits.includes(appraisal.id)) {
      return false;
    }

    const foundMatchingEval = appraisal.employees.find(
      empEvalData => empEvalData.evaluations.find(
        tplEval => selectedTemplateIds.includes(tplEval.templateId),
      ),
    );

    return !!foundMatchingEval;
  });
};

export const mapEvaluationData = (
  rawAppraisals: ReportEvaluationType[],
  myUnits: number[],
  selectedTemplateIds: string[],
  selectedTemplateFields: TemplateFieldSelection,
  allTemplates: any[],
): ITemplateReportData[] => {
  const foundTemplate = allTemplates.find(
    templateItem => selectedTemplateIds[0] && templateItem.id === selectedTemplateIds[0],
  );

  return filterEvaluations(rawAppraisals, myUnits, selectedTemplateIds).reduce(
    (acc: ITemplateReportData[], evaluationItem: ReportEvaluationType) => {
      if (!evaluationItem.employees) {
        return acc;
      }

      const validAppraisals: ITemplateReportData[] = evaluationItem.employees
        .filter((appraisalEmpData: any) => {
          return (
            appraisalEmpData.evaluations &&
            appraisalEmpData.evaluations.length !== undefined
          );
        })
        .reduce((evalAcc: ITemplateReportData[], appraisalEmpData: any) => {
          const empInfoData = getEmployeeInfoById(appraisalEmpData.employee.id);
          if (appraisalEmpData.evaluations.length === 0) {
            return evalAcc.concat([
              {
                unitId: evaluationItem.id,
                unitName: evaluationItem.unitName,
                empId: appraisalEmpData.employee.id,
                empNumber: empInfoData ? empInfoData.fEmpPersonNumber : '',
                evalItems: {},
                name: `${appraisalEmpData.employee.firstName} ${appraisalEmpData.employee.lastName}`,
                position: empInfoData ? empInfoData.fEmpPosition : '',
                selectedTemplateFields: selectedTemplateFields,
                statusCode: EvaluationStatus.NOT_STARTED,
                templateId: '',
                template: foundTemplate,
                hasAccount: appraisalEmpData.employee.hasAccount,
              },
            ]);
          }

          const mappedAppraisalEvals = appraisalEmpData.evaluations.map(
            (appraisalEvalItem: any) => ({
              unitId: evaluationItem.id,
              unitName: evaluationItem.unitName,
              empId: appraisalEmpData.employee.id,
              empNumber: empInfoData ? empInfoData.fEmpPersonNumber : '',
              evalItems: appraisalEvalItem,
              name: `${appraisalEmpData.employee.firstName} ${appraisalEmpData.employee.lastName}`,
              position: empInfoData ? empInfoData.fEmpPosition : '',
              selectedTemplateFields: selectedTemplateFields,
              statusCode: appraisalEvalItem.status
                ? appraisalEvalItem.status
                : EvaluationStatus.NOT_STARTED,
              templateId: appraisalEvalItem.templateId
                ? appraisalEvalItem.templateId
                : '',
              template: foundTemplate,
              hasAccount: appraisalEmpData.employee.hasAccount,
            }),
          );

          return evalAcc.concat(mappedAppraisalEvals);
        }, []);

      return acc.concat(validAppraisals);
    },
    [],
  );
};

export const mapEvaluationDataForUI = (d: any): ReportEvaluationType[] => {
  return d.map((data: any) => {
    return {
      id: data.unit.id,
      unitName: data.unit.unitName,
      employees: data.employees,
    };
  });
};

export const getUserRolePerspective = (relation: Relation) => {
  if (relation !== Relation.ALL) {
    return 'manager';
  }

  return isHR() ? 'hr' : 'manager';
};

const isTplEvaluationStarted = (
  templateId: string | number,
  evaluations: GenericEvaluationItem[],
) => {
  return evaluations.some(evalItem => evalItem.templateId === `${templateId}`);
};

export const getUnitGridData = <ReportData extends ITemplateReport>(
  processedEvaluationData: ReportData[],
  selectedTemplateIds: string[],
) => {
  return processedEvaluationData.reduce(
    (
      acc: IUnitGridData[],
      processedEvalItem: ReportData,
      _index: number,
      initialProcessedEvalData: ReportData[],
    ) => {
      const alreadyAdded = acc.find(
        (accItem: IUnitGridData) =>
          accItem.unitId === processedEvalItem.unitId,
      );
      if (alreadyAdded) {
        return acc;
      }

      const statusCount = initialProcessedEvalData.reduce(
        (accCount, currentItem) => {
          if (
            !selectedTemplateIds.includes(currentItem.templateId) ||
            currentItem.unitId !== processedEvalItem.unitId
          ) {
            return accCount;
          }

          if (currentItem.statusCode === EvaluationStatus.COMPLETED) {
            return {
              ...accCount,
              completed: [ ...accCount.completed, currentItem.empId ],
            };
          }

          if (currentItem.statusCode === EvaluationStatus.IN_PROGRESS) {
            return {
              ...accCount,
              inProgress: [ ...accCount.inProgress, currentItem.empId ],
            };
          }

          return accCount;
        },
        { inProgress: [], completed: [], notStarted: [] },
      );

      return acc.concat([
        {
          unitId: processedEvalItem.unitId,
          unitName: processedEvalItem.unitName,
          completed: statusCount.completed,
          inProgress: statusCount.inProgress,
          notStarted: statusCount.notStarted,
        },
      ]);
    },
    [],
  );
};

const isEmpInCombinedAudience = (
  selectedTemplateIds: string[],
  templates: ITemplateFull[],
  employeeDetails: SummaryEmpData,
) => {
  const combinedAudience = getCombinedAudienceUnion(
    selectedTemplateIds,
    templates,
  );
  
  return isEmpTargetedByTplAudience(
    combinedAudience.unitAudience,
    combinedAudience.roleAudience,
    employeeDetails,
  );
};

const getEvaluationsStatusDistribution = (
  empEvaluations: GenericEvaluationItem[],
  employeeDetails: SummaryEmpData,
  selectedTemplateIds: string[],
) => {
  return selectedTemplateIds.reduce((acc, selectedTemplateId) => {
    if (!isTplEvaluationStarted(selectedTemplateId, empEvaluations)) {
      return {
        ...acc,
        notStarted: [ ...acc.notStarted, employeeDetails.empId ],
      };
    }

    const currentEvalStatusDistribution = empEvaluations.reduce((empEvalAcc, empEval) => {
      if (!isTplEvaluationStarted(selectedTemplateId, [empEval])) {
        return empEvalAcc;
      }

      if (empEval.status) {
        return {
          ...empEvalAcc,
          inProgress: empEval.status === EvaluationStatus.IN_PROGRESS
            ? [ ...empEvalAcc.inProgress, employeeDetails.empId ]
            : [ ...empEvalAcc.inProgress ],
          completed: empEval.status === EvaluationStatus.COMPLETED
            ? [ ...empEvalAcc.completed, employeeDetails.empId ]
            : [ ...empEvalAcc.completed ],
        };
      }

      if (
        !empEval.approvedBy ||
        !empEval.approvedBy.length
      ) {
        return {
          ...empEvalAcc,
          inProgress: [ ...empEvalAcc.inProgress, employeeDetails.empId ],
        };
      }

      return {
        ...empEvalAcc,
        completed: [ ...empEvalAcc.completed, employeeDetails.empId ],
      };
    }, emptyDistribution);

    return {
      ...acc,
      inProgress: [...acc.inProgress, ...currentEvalStatusDistribution.inProgress],
      completed: [...acc.completed, ...currentEvalStatusDistribution.completed],
    };
  }, emptyDistribution);
};

export const getUnitEvaluationCombinedStatusDistribution = (
  empEvaluations: GenericEmployeeEvaluation[],
  templates: ITemplateFull[],
  currentUnit: OrgUnit,
  selectedTemplateIds: string[],
): EvaluationStatusDistribution => {
  return empEvaluations.reduce(
    (accCombinedDistribution, empEvalDataItem: GenericEmployeeEvaluation) => {
      if (
        !isEmpInCombinedAudience(selectedTemplateIds, templates, empEvalDataItem.employeeDetails)
        || currentUnit.fTreeUnitId !== `${empEvalDataItem.employeeDetails.orgUnitDetails.id}`
      ) {
        return accCombinedDistribution;
      }

      const empGoalEvalStatuses = getEvaluationsStatusDistribution(
        empEvalDataItem.evaluations,
        empEvalDataItem.employeeDetails,
        selectedTemplateIds,
      );

      return {
        ...accCombinedDistribution,
        notStarted: accCombinedDistribution.notStarted.concat(empGoalEvalStatuses.notStarted),
        inProgress: accCombinedDistribution.inProgress.concat(empGoalEvalStatuses.inProgress),
        completed: accCombinedDistribution.completed.concat(empGoalEvalStatuses.completed),
      };
    }, emptyDistribution);
};

export function filterEvaluationData<EmpEvaluation extends GenericEmployeeEvaluation>(
  evaluationData: EmpEvaluation[],
  tplIds: string[],
  includedUnitIds: string[],
  unitGridData: IUnitGridData[],
): EmpEvaluation[] {
  const audienceIds = includedUnitIds.reduce((acc, inclUnitId) => {
    const currentUnitGridData: IUnitGridData = unitGridData.find(ug => `${ug.unitId}` === inclUnitId);
    if (!currentUnitGridData) {
      return acc;
    }

    return acc.concat([
      ...currentUnitGridData.notStarted,
      ...currentUnitGridData.inProgress,
      ...currentUnitGridData.completed,
    ]);
  }, []);

  return evaluationData.filter((evaluationDataItem) => (
    audienceIds.includes(evaluationDataItem.employeeDetails.empId)
    && includedUnitIds.includes(
      `${evaluationDataItem.employeeDetails.orgUnitDetails.id}`,
    )
  )).map((evaluationDataItem) => ({
    ...evaluationDataItem,
    evaluations: evaluationDataItem.evaluations.filter(
      evalItem => tplIds.includes(evalItem.templateId),
    ),
  }));
}

export const templateStatusTranslation = (status: TemplateStatus) => {
  switch (status) {
    case TemplateStatus.ACTIVE: return translate.t('laActive');
    case TemplateStatus.INACTIVE: return translate.t('laInactive');
    default : return status;
  }
};

export const translateCompletionStatus = function(status: EvaluationStatus) {
  switch (status) {
    case EvaluationStatus.IN_PROGRESS: return translate.t('laInProgress');
    case EvaluationStatus.COMPLETED: return translate.t('laCompleted');
    case EvaluationStatus.NOT_STARTED: return translate.t('laNotStarted');
    default : return status;
  }
};

export const getEnumValue = (
  enumCode: string,
  enumGroupKey: string,
  allEnums: any,
) => {
  if (!enumCode || !enumGroupKey) {
    return '';
  }

  if (!allEnums[enumGroupKey]) {
    return enumCode;
  }

  return getEnumName(enumGroupKey, enumCode, allEnums);
};

export const searchMatchingTemplates = (templates: ITemplate[], searchQuery: string): ITemplate[] => {
  return templates.filter((template: ITemplate) => {
    return `${template.subject}`.toLocaleLowerCase().indexOf(
      `${searchQuery}`.toLocaleLowerCase()
    ) > -1;
  });
};

export const isEmpTargetedByTplAudience = (
  unitAudience: number[],
  roleAudience: string[],
  employeeDetails: SummaryEmpData,
) => {
  const hasUnitAudience = unitAudience && unitAudience.length;
  const hasRoleAudience = roleAudience && roleAudience.length;

  if (!hasUnitAudience && !hasRoleAudience) {
    return true;
  }

  if (!employeeDetails
      || !employeeDetails.orgUnitDetails
      || !employeeDetails.userDetails
  ) {
    return false;
  }

  const isTargetedFromUnit = hasUnitAudience
    && unitAudience.includes(employeeDetails.orgUnitDetails.id);
  const isTargetedFromRole = hasRoleAudience
    && roleAudience.some(role => employeeDetails.userDetails.roles.includes(role));

  return isTargetedFromUnit || isTargetedFromRole;
};

export const getCombinedAudienceUnion = (
  selectedTemplateIds: string[],
  goalTemplates: ITemplateFull[],
) => {
  const fullAudience: {
    unitAudience: number[];
    roleAudience: string[];
  } = { unitAudience: [], roleAudience: [] };

  if (!selectedTemplateIds.length) {
    return fullAudience;
  }

  if (selectedTemplateIds.length === 1) {
    return goalTemplates.find(
      (tpl: any) => tpl.id === selectedTemplateIds[0]
    ).audience;
  }

  const foundFullAudience = selectedTemplateIds.find((tplId) => {
    const foundTemplateData: ITemplateFull | undefined = goalTemplates.find(
      (tpl: any) => tpl.id === tplId
    );

    if (!foundTemplateData || !foundTemplateData.audience) {
      return false;
    }

    return foundTemplateData.audience.roleAudience.length === 0
      || foundTemplateData.audience.unitAudience.length === 0;
  });

  if (foundFullAudience) {
    return fullAudience;
  }

  return selectedTemplateIds.reduce((acc, tplId) => {
    const foundTemplateData: ITemplateFull | undefined = goalTemplates.find(
      (tpl: any) => tpl.id === tplId
    );

    if (!foundTemplateData || !foundTemplateData.audience) {
      return acc;
    }

    return {
      unitAudience: acc.unitAudience.concat(foundTemplateData.audience.unitAudience),
      roleAudience: acc.roleAudience.concat(foundTemplateData.audience.roleAudience),
    };
  }, fullAudience);
};

export const getTemplateSubjectById = (templateId: string, templates: ITemplateFull[]) => {
  const found = templates.find(
    (t: ITemplateFull) => t.id.toString() === templateId,
  );

  return found ? found.subject : '';
};

export const getEvaluationStatus = (empData: SummaryEmpData, unitGridData: IUnitGridData[]) => {
  if (!empData || !empData.orgUnitDetails || !unitGridData) {
    return '';
  }

  const foundUnit = unitGridData.find(unitGridItem => unitGridItem.unitId === empData.orgUnitDetails.id);
  if (!foundUnit) {
    return '';
  }

  if (foundUnit.completed.includes(empData.empId)) {
    return translate.t('laCompleted');
  }

  if (foundUnit.inProgress.includes(empData.empId)) {
    return translate.t('laInProgress');
  }

  if (foundUnit.notStarted.includes(empData.empId)) {
    return translate.t('laNotStarted');
  }

  return '';
};
